import { ClofoApiError } from './clofo-api'
import { getLoginUrl, getPreviewTag } from '../lib'

type JsonApiError = {
  detail?: string
  source?: { pointer?: string }
  [key: string]: unknown
}

type ApiErrorResponse = {
  // JSON:API format: { errors: [{ detail, source, ... }] }
  // REST format:     { errors: { field: ["msg"], non_field_errors: [...] } }
  errors?:
    | JsonApiError[]
    | {
        non_field_errors?: string[]
        [key: string]: string[] | undefined
      }
  non_field_errors?: string[]
  error?: string[]
}
interface QueryParams {
  [key: string]: string | number | boolean | undefined
}

// TODO: Implement timeout handling
// const DEFAULT_TIMEOUT_MS = 10000

let redirectOn401 = true
const onUnauthorizedCallbacks: Array<() => void | Promise<void>> = []

/**
 * Certain endpoints reuse 401 for a domain signal (e.g. adobe_auth_expired)
 * which does not signify "clofo session invalid". Skip global unauthorized handling
 * if error detail is included in ALLOWLIST below
 *
 * */
const KNOWN_NON_SESSION_401_DETAILS = new Set(['adobe_auth_expired', 'Invalid login attempt'])

export const http = {
  onUnauthorized(callback: () => void | Promise<void>) {
    onUnauthorizedCallbacks.push(callback)
  },

  get(url: string, params?: QueryParams, dealId?: number) {
    const queryString = params ? `?${buildQueryString(params)}` : ''
    return makeRequest(`${url}${queryString}`, 'GET', undefined, dealId)
  },

  post(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'POST', body, dealId)
  },

  patch(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'PATCH', body, dealId)
  },

  put(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'PUT', body, dealId)
  },

  putFile(url: string, file: File | Blob, contentType?: string) {
    return makeRequest(url, 'PUT', file, undefined, contentType || file.type)
  },

  delete(url: string, dealId?: number) {
    return makeRequest(url, 'DELETE', undefined, dealId)
  },

  setRedirectOn401(value: boolean) {
    redirectOn401 = value
  },

  redirectToLoginWithNextUrl() {
    const { pathname, search, hash } = window.location
    // The search is kept only in previews: its `reactTag` param is what returns the user to the
    // preview build after login. The production format deliberately carries just path + hash.
    const currentUrl = getPreviewTag() ? `${pathname}${search}${hash}` : `${pathname}${hash}`
    window.location.href = getLoginUrl(window.btoa(currentUrl))
  },
}

async function makeRequest(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: Record<string, unknown> | File | Blob,
  dealId?: number,
  contentType?: string,
): Promise<unknown> {
  const isFileUpload = body instanceof File || body instanceof Blob

  const requestOptions: RequestInit = {
    method,
    headers: {
      ...(isFileUpload
        ? contentType && { 'Content-Type': contentType }
        : { 'Content-Type': contentType || 'application/json' }),
      ...(dealId && { 'X-Deal-ID': dealId.toString() }),
    },
    ...(body && {
      body: isFileUpload ? body : JSON.stringify(body),
    }),
    credentials: isFileUpload ? 'omit' : 'include',
    // signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
  }

  const response = await fetch(url, requestOptions)

  /**
   * TODO: Implement token refresh logic on 401 responses
   */
  if (response.status === 401) {
    let skipUnauthorizedHandling = false
    try {
      const body = (await response.clone().json()) as { detail?: string } | null
      skipUnauthorizedHandling = !!body?.detail && KNOWN_NON_SESSION_401_DETAILS.has(body.detail)
    } catch {
      // Non-JSON or empty 401 body — fall through to normal handling below.
    }

    if (!skipUnauthorizedHandling) {
      await Promise.all(onUnauthorizedCallbacks.map(callback => callback()))
      if (redirectOn401) {
        http.redirectToLoginWithNextUrl()
        // throw an error if the redirect doesn't happen within 10 seconds
        await new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('Timed out waiting to redirect on a 401 response')),
            10000,
          )
        })
      }
    }
  }

  // Handle successful responses
  if (response.ok) {
    if (response.status === 204) {
      return null
    }

    if (isFileUpload) {
      return response
    }

    return await response.json()
  }

  // Handle error responses
  let errorData: ApiErrorResponse | null = null
  let errorCode: string = ''

  // extract the error message from the response
  if (response.status === 400 || response.status === 422) {
    errorData = await response.json()
    const errors = errorData?.errors
    if (Array.isArray(errors)) {
      // JSON:API format: { errors: [{ detail: "..." }] }
      errorCode = errors[0]?.detail || ''
    } else if (errors?.non_field_errors) {
      errorCode = errors.non_field_errors[0] || ''
    } else if (errorData?.non_field_errors) {
      errorCode = errorData.non_field_errors[0] || ''
    } else if (errorData?.error) {
      errorCode = errorData.error[0] || ''
    } else if (errors) {
      const firstFieldErrors = Object.values(errors).find(v => Array.isArray(v) && v.length)
      errorCode = Array.isArray(firstFieldErrors) ? firstFieldErrors[0] || '' : ''
    }
  }

  throw new ClofoApiError({
    status: response.status,
    statusText: response.statusText,
    method,
    endpoint: url.substring(url.indexOf('/api/')),
    url,
    errorCode,
    errorData,
  })
}

function buildQueryString(params: QueryParams): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}
